<script lang="ts">
import { getEnv } from '@monogrid/env'
import { useGlobalScrollLock, usePlatform } from '@monogrid/vue-boost/core'
import { type UseLenis, useLenis } from '@monogrid/vue-boost/integrations/lenis'
import { templateRef, useElementSize } from '@vueuse/core'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { ComputedRef, InjectionKey, Ref } from 'vue'
import { computed, defineComponent, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import shareImageTODOChangeMe from '@/assets/share.jpg'
import ScrollProgress from '@/components/atoms/ScrollProgress.vue'
import LanguageBanner from '@/components/molecules/LanguageBanner.vue'
import { flippingProjects } from '@/components/organisms/sections/project/NextProject.vue'
import SiteLayout from '@/components/organisms/SiteLayout.vue'
import SiteLoader, { type SiteLoaderPublicInstance } from '@/components/organisms/SiteLoader.vue'
import { setupHead } from '@/functions/useHead'
import { type GetSettingsQuery, useGetSettingsQuery } from '@/graphql'
import WebGLApp from '@/three/WebGLApp.vue'
import { getScrollbarWidth } from '@/utils'

import { trackEvent } from './directives/track'
import { useQueryVariables } from './functions/useQueryVariables'
export const lenisInjectionKey: InjectionKey<UseLenis> = Symbol('lenis')
export const loaderInjectionKey: InjectionKey<Ref<SiteLoaderPublicInstance>> = Symbol('loader')
export const settingsInjectionKey: InjectionKey<ComputedRef<GetSettingsQuery['content'] & {offices: GetSettingsQuery['offices']['items']}>> = Symbol('settings')

export default defineComponent({
  name: 'App',
  components: {
    LanguageBanner,
    ScrollProgress,
    SiteLayout,
    SiteLoader,
    WebGLApp
  },
  /**
   * Introduction
   *
   * https://v3.vuejs.org/guide/composition-api-introduction.html#basics-of-composition-api
   *
   * Setup functions
   * https://v3.vuejs.org/guide/composition-api-setup.html
   */
  setup (props, { attrs, slots, emit }) {
    const router = useRouter()
    const route = useRoute()
    const env = getEnv()
    const loader = templateRef<SiteLoaderPublicInstance>('loader')
    const settingsQuery = useGetSettingsQuery({ variables: useQueryVariables() })
    const settings = computed(() => ({
      ...settingsQuery.data.value?.content,
      offices: (settingsQuery.data.value?.offices?.items || []).sort((a, b) => a.country.toLowerCase() === 'it' ? -1 : 0)
    }))
    const platform = usePlatform()
    const lenis = useLenis()
    const scrollLocked = useGlobalScrollLock()
    const { width: domWidth, height: domHeight } = useElementSize(document.documentElement)
    const scrollbarWidth = ref(0)
    const domScrollable = ref()

    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        mutation.addedNodes.forEach((addedNode: Element) => {
          if (addedNode.id !== 'iubenda-iframe') return
          addedNode.setAttribute('data-lenis-prevent', '')
        })
      })
    })

    observer.observe(document.body, { subtree: false, childList: true })

    setupHead({
      siteName: () => 'MONOGRID',
      siteDescription: () => 'Digital boutique agency',
      defaultImage: () => `https://${env.SERVICE_TARGET_DOMAIN}${shareImageTODOChangeMe}`
    })

    router.beforeEach(async (to, from) => {
      if (to.name !== from?.name || (to.name === 'Project' && !flippingProjects.value)) {
        await new Promise((resolve) => loader.value?.show(() => resolve(true)))
      }
    })

    const unwatch = watch(loader, loaderInstance => {
      if (!loaderInstance) return
      loaderInstance.show()
      unwatch()
    })

    watch(platform.mobile, mobile => mobile ? lenis.destroy() : lenis.restart(), { immediate: true })

    watch([domWidth, domHeight], ([w, h]) => {
      domScrollable.value = document.documentElement.scrollHeight > document.documentElement.clientHeight
      scrollbarWidth.value = getScrollbarWidth()
    }, { immediate: true })

    watch([scrollLocked, domScrollable], ([locked, scrollable]) => {
      const padding = locked && scrollable && !platform.mobile.value ? `${scrollbarWidth.value}px` : '0px'
      document.documentElement.style.setProperty('--scrollbar-placeholder', padding)
    }, { flush: 'pre', immediate: true })

    // Test tracking
    watch([() => route.name, () => route.params.slug], ([name, slug]) => {
      trackEvent({
        event: 'page_change_event',
        eventAction: 'Enter Page',
        eventLabel: [name, slug].join(' ')
      })
    }, { immediate: true })

    if ('fonts' in document) {
      Promise.all([
        document.fonts.load('1em "Space Mono"'),
        document.fonts.load('1em "HelveticaNeue"'),
        document.fonts.load('300 1em "HelveticaNeue"'),
        document.fonts.load('700 1em "HelveticaNeue"')
      ]).then(() => ScrollTrigger.refresh()).catch(null)
    }

    provide(lenisInjectionKey, lenis)
    provide(loaderInjectionKey, loader)
    provide(settingsInjectionKey, settings)

    return {
      settings
    }
  }
})
</script>

<template>
  <WebGLApp>
    <SiteLayout v-if="settings">
      <RouterView v-slot="{ Component, route }">
        <Transition mode="out-in">
          <component
            :is="Component"
            v-if="(!route.meta || (route.meta && route.meta.loadingChunk === false)) && Component"
            :key="route.name + route.params.locale"
            class="page relative"
          />
        </Transition>
      </RouterView>
    </SiteLayout>
  </WebGLApp>
  <LanguageBanner />
  <SiteLoader ref="loader" />
  <ScrollProgress />
</template>

<style lang="scss">
@import "@/styles/main";

html {
  box-sizing: border-box;
  padding-right: var(--scrollbar-placeholder, 0);
}
</style>

<style lang="scss" scoped>

.app-container {
  min-height: 100vh;
  min-height: 100dvh;

  @media screen and (min-width: #{map-get($breakpoints, 'lg')}px) {
    font-size: 50%;
  }
}

.loading-page-chunk {
  text-align: center;
}
</style>
